import { Link } from 'react-router-dom';
import { MetricCard } from '@/components/ui/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type { OrdersResponse } from '@/lib/types.js';
import {
  OrderTable,
  SectionEmpty,
  SectionPager,
  SectionTitle,
  planLabel,
  type SectionPaging,
} from './shared.js';

/**
 * Stripe 海外订单 section. USD revenue + monthly/yearly breakdown +
 * paginated rows from audit_logs `stripe.subscription_granted`.
 */
export function StripeSection({
  data,
  offset,
  onOffsetChange,
  pageSize,
}: { data: OrdersResponse['stripe'] } & SectionPaging): JSX.Element {
  const { fmtDateTime } = useFmt();
  return (
    <section className="mb-8">
      <SectionTitle
        icon="💳"
        title="Stripe 订单（海外）"
        emptyHint={data.total === 0 ? '暂无' : undefined}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="USD 营收"
          value={`$${data.revenue_usd.toFixed(2)}`}
          tone={data.revenue_usd > 0 ? 'success' : 'neutral'}
        />
        <MetricCard
          label="付款次数"
          value={data.total}
          unit="笔"
          tone={data.total > 0 ? 'info' : 'neutral'}
          hint="含初次 + 每月续费"
        />
        <MetricCard label="月卡" value={data.by_plan.monthly} unit="笔" tone="neutral" />
        <MetricCard label="年卡" value={data.by_plan.yearly} unit="笔" tone="neutral" />
      </div>

      {data.orders.length === 0 ? (
        <SectionEmpty>还没收到 Stripe 付费。等海外用户 checkout 后这里会自动出现。</SectionEmpty>
      ) : (
        <OrderTable>
          <thead className="bg-white/[0.04]">
            <tr>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">用户</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">套餐</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">金额</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">续期到</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">Stripe Session</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">付款时间</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.audit_id} className="border-t border-white/[0.08]">
                <td className="px-4 py-3">
                  <Link to={`/users/${o.user_id}`} className="text-shark-300 hover:text-shark-200">
                    {o.user_email ?? o.user_id.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-300">{planLabel(o.plan)}</td>
                <td className="px-4 py-3 text-shark-300 font-semibold tabular-nums">
                  ${(o.amount_usd_cents / 100).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-slate-400">
                  {o.new_expires_at !== null ? fmtDateTime(o.new_expires_at) : '—'}
                </td>
                <td className="px-4 py-3 font-mono text-[10px] text-slate-500 break-all max-w-[200px]">
                  {o.stripe_session_id ?? '—'}
                </td>
                <td className="px-4 py-3 text-slate-400">{fmtDateTime(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </OrderTable>
      )}

      <SectionPager total={data.total} paging={{ offset, onOffsetChange, pageSize }} />
    </section>
  );
}
